import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, ProgressBar, Row, Spinner, Table } from 'react-bootstrap'
import { Link, useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { PROJECT_STATUS, beYearOptions, currentBeYear, fmtBaht } from './aoShared'

const emptyForm = {
  pj_year: String(currentBeYear()), pj_name: '', pj_department: '', pj_owner_t_id: '',
  pj_objective: '', pj_budget: '', pj_start: '', pj_end: '',
}

// ธุรการ: ทะเบียนโครงการ / แผนปฏิบัติการประจำปี
export default function Projects() {
  const navigate = useNavigate()
  const [year, setYear] = useState(String(currentBeYear()))
  const [status, setStatus] = useState('')
  const [q, setQ] = useState('')
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [file, setFile] = useState(null)
  const [teachers, setTeachers] = useState([])
  const [saving, setSaving] = useState(false)
  const [modalError, setModalError] = useState('')

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const params = new URLSearchParams()
      if (year) params.set('year', year)
      if (status) params.set('status', status)
      if (q) params.set('q', q)
      const res = await axiosInstance.get(`/api/ao/projects?${params.toString()}`)
      if (res.data.success) setProjects(res.data.data)
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [year, status, q])

  useEffect(() => { fetchData() }, [fetchData])

  const openModal = async () => {
    setForm({ ...emptyForm, pj_year: year })
    setFile(null)
    setModalError('')
    setShowModal(true)
    if (!teachers.length) {
      try {
        const res = await axiosInstance.get('/api/ao/teachers')
        if (res.data.success) setTeachers(res.data.data)
      } catch { /* ignore */ }
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setModalError('')
    try {
      const fd = new FormData()
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ''))
      if (file) fd.append('file', file)
      const res = await axiosInstance.post('/api/ao/projects', fd)
      if (res.data.success) {
        setShowModal(false)
        Swal.fire({ icon: 'success', title: 'บันทึกโครงการเรียบร้อย', timer: 1800, showConfirmButton: false })
        fetchData()
      }
    } catch (err) {
      setModalError(err.response?.data?.message || 'บันทึกไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  const totalBudget = projects.reduce((s, p) => s + Number(p.pj_budget || 0), 0)
  const totalSpent = projects.reduce((s, p) => s + Number(p.pj_spent || 0), 0)

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
            <h4 className="main-title mb-0">
              <i className="ri-bar-chart-box-line me-2 text-primary" />
              แผนงาน / โครงการ ประจำปี
            </h4>
            <div className="d-flex gap-2">
              <Button as={Link} to="/admin-office/annual-summary" variant="outline-primary" size="sm">
                <i className="ri-file-chart-line me-1" />สรุปประจำปี
              </Button>
              <Button variant="primary" size="sm" onClick={openModal}>
                <i className="ri-add-line me-1" />เพิ่มโครงการ
              </Button>
            </div>
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          <Card className="card-one">
            <Card.Header className="d-flex align-items-center gap-2 flex-wrap">
              <Form.Select size="sm" style={{ width: 120 }} value={year} onChange={e => setYear(e.target.value)}>
                {beYearOptions().map(y => <option key={y} value={y}>พ.ศ. {y}</option>)}
              </Form.Select>
              <Form.Select size="sm" style={{ width: 160 }} value={status} onChange={e => setStatus(e.target.value)}>
                <option value="">ทุกสถานะ</option>
                {Object.entries(PROJECT_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </Form.Select>
              <Form.Control size="sm" style={{ maxWidth: 220 }} placeholder="ค้นหา ชื่อโครงการ/ผู้รับผิดชอบ"
                value={q} onChange={e => setQ(e.target.value)} />
              <div className="ms-auto text-muted fs-sm">
                {projects.length} โครงการ · งบ {fmtBaht(totalBudget)} · เบิกจ่าย {fmtBaht(totalSpent)} บาท
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
              ) : projects.length === 0 ? (
                <div className="text-center text-muted py-4">ไม่พบโครงการในเงื่อนไขที่เลือก</div>
              ) : (
                <Table responsive hover className="mb-0 fs-sm">
                  <thead className="table-light">
                    <tr>
                      <th>ชื่อโครงการ</th>
                      <th style={{ width: 130 }}>ฝ่าย</th>
                      <th style={{ width: 140 }}>ผู้รับผิดชอบ</th>
                      <th className="text-end" style={{ width: 120 }}>งบประมาณ</th>
                      <th style={{ width: 160 }}>เบิกจ่าย</th>
                      <th style={{ width: 130 }}>สถานะ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.map(p => {
                      const st = PROJECT_STATUS[p.pj_status] || PROJECT_STATUS.planned
                      const budget = Number(p.pj_budget || 0)
                      const spent = Number(p.pj_spent || 0)
                      const pct = budget > 0 ? Math.min(100, Math.round(spent / budget * 100)) : 0
                      return (
                        <tr key={p.pj_id} style={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/admin-office/projects/${p.pj_id}`)}>
                          <td className="fw-semibold text-truncate" style={{ maxWidth: 260 }} title={p.pj_name}>
                            <Link to={`/admin-office/projects/${p.pj_id}`} onClick={e => e.stopPropagation()}>{p.pj_name}</Link>
                          </td>
                          <td>{p.pj_department || '-'}</td>
                          <td>{p.owner_name || '-'}</td>
                          <td className="text-end">{fmtBaht(budget)}</td>
                          <td>
                            <ProgressBar now={pct} variant={pct > 100 ? 'danger' : 'info'} style={{ height: 8 }} className="mb-1" />
                            <div className="text-muted" style={{ fontSize: '0.72rem' }}>{fmtBaht(spent)} ({pct}%)</div>
                          </td>
                          <td>
                            <Badge bg={st.bg}>{st.label}</Badge>
                            {p.pj_eval_score && <div className="text-muted" style={{ fontSize: '0.72rem' }}>ประเมิน {p.pj_eval_score}/5</div>}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Container>
      </div>
      <Footer />

      {/* Create modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">เพิ่มโครงการ</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {modalError && <Alert variant="danger" className="py-2">{modalError}</Alert>}
            <Row className="g-2 mb-2">
              <Col xs={3}>
                <Form.Label className="fs-sm mb-1">ปี (พ.ศ.) <span className="text-danger">*</span></Form.Label>
                <Form.Select size="sm" value={form.pj_year}
                  onChange={e => setForm(f => ({ ...f, pj_year: e.target.value }))}>
                  {beYearOptions().map(y => <option key={y} value={y}>{y}</option>)}
                </Form.Select>
              </Col>
              <Col xs={9}>
                <Form.Label className="fs-sm mb-1">ชื่อโครงการ <span className="text-danger">*</span></Form.Label>
                <Form.Control size="sm" type="text" maxLength={500} required value={form.pj_name}
                  onChange={e => setForm(f => ({ ...f, pj_name: e.target.value }))} />
              </Col>
            </Row>
            <Row className="g-2 mb-2">
              <Col xs={4}>
                <Form.Label className="fs-sm mb-1">ฝ่าย/กลุ่มงาน</Form.Label>
                <Form.Control size="sm" type="text" maxLength={100} value={form.pj_department}
                  placeholder="เช่น ฝ่ายวิชาการ" list="ao-departments"
                  onChange={e => setForm(f => ({ ...f, pj_department: e.target.value }))} />
              </Col>
              <Col xs={4}>
                <Form.Label className="fs-sm mb-1">ผู้รับผิดชอบ</Form.Label>
                <Form.Select size="sm" value={form.pj_owner_t_id}
                  onChange={e => setForm(f => ({ ...f, pj_owner_t_id: e.target.value }))}>
                  <option value="">— ไม่ระบุ —</option>
                  {teachers.map(t => <option key={t.t_id} value={t.t_id}>{t.t_name}</option>)}
                </Form.Select>
              </Col>
              <Col xs={4}>
                <Form.Label className="fs-sm mb-1">งบประมาณ (บาท)</Form.Label>
                <Form.Control size="sm" type="number" min="0" step="0.01" value={form.pj_budget}
                  onChange={e => setForm(f => ({ ...f, pj_budget: e.target.value }))} />
              </Col>
            </Row>
            <Row className="g-2 mb-2">
              <Col xs={4}>
                <Form.Label className="fs-sm mb-1">เริ่มดำเนินการ</Form.Label>
                <Form.Control size="sm" type="date" value={form.pj_start}
                  onChange={e => setForm(f => ({ ...f, pj_start: e.target.value }))} />
              </Col>
              <Col xs={4}>
                <Form.Label className="fs-sm mb-1">สิ้นสุด</Form.Label>
                <Form.Control size="sm" type="date" value={form.pj_end}
                  onChange={e => setForm(f => ({ ...f, pj_end: e.target.value }))} />
              </Col>
              <Col xs={4}>
                <Form.Label className="fs-sm mb-1">เล่มโครงการ (ไฟล์)</Form.Label>
                <Form.Control size="sm" type="file" accept=".pdf,.doc,.docx"
                  onChange={e => setFile(e.target.files[0] || null)} />
              </Col>
            </Row>
            <Form.Group>
              <Form.Label className="fs-sm mb-1">วัตถุประสงค์</Form.Label>
              <Form.Control size="sm" as="textarea" rows={2} value={form.pj_objective}
                onChange={e => setForm(f => ({ ...f, pj_objective: e.target.value }))} />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={() => setShowModal(false)}>ยกเลิก</Button>
            <Button variant="primary" size="sm" type="submit" disabled={saving}>
              {saving ? <Spinner size="sm" className="me-1" /> : <i className="ri-save-line me-1" />}
              บันทึกโครงการ
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  )
}
